import React, {useEffect, useState} from 'react'
import {Container} from 'semantic-ui-react'
import {useLocation} from 'react-router-dom'
import {ErrorMessage} from '../error-message'
import './style.css'
import {captureException} from '@sentry/react'

export interface IErrorBoundaryProps {
  children: React.ReactNode
}

export interface IErrorBoundaryInternalProps extends IErrorBoundaryProps {
  hasError: boolean
  setHasError: (value: boolean) => void
}

export interface IErrorBoundaryState {
  hasError: boolean
  message?: string
}

class ErrorBoundaryInternal extends React.Component<
  IErrorBoundaryInternalProps,
  IErrorBoundaryState
> {
  constructor(props: IErrorBoundaryInternalProps) {
    super(props)
    this.state = {
      hasError: props.hasError,
    }
  }

  static getDerivedStateFromError(error: Error) {
    console.error('Error caught:', error)
    // TODO: sentry in local dev envs?
    if (process.env.NODE_ENV !== 'development') {
      captureException(error)
    }
    return {
      hasError: true,
      message: error.message,
    } as IErrorBoundaryState
  }

  componentDidUpdate(prevProps: IErrorBoundaryInternalProps) {
    if (!this.props.hasError && prevProps.hasError) {
      this.setState({
        hasError: false,
      })
    }
  }

  componentDidCatch() {
    this.props.setHasError(true)
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Container className='route error'>
          <ErrorMessage message={this.state.message || 'Unknown error'} />
        </Container>
      )
    }
    const {children} = this.props
    if (!children) return null
    return <>{children}</>
  }
}

export function ErrorBoundary(props: IErrorBoundaryProps) {
  // return props.children;
  const location = useLocation()
  const [hasError, setHasError] = useState(false)
  useEffect(() => {
    if (hasError) setHasError(false)
  }, [location.key])
  return (
    <ErrorBoundaryInternal
      hasError={hasError}
      setHasError={setHasError}
      {...props}
    />
  )
}
